import React, { useState, useEffect } from 'react';
import type { TeamMember, TeamMemberStatus } from '../../services/timetablesService';
import { timetablesService } from '../../services/timetablesService';

interface TeamMemberListProps {
  teamMembers: TeamMember[];
  onEdit: (member: TeamMember) => void;
  onDelete: (memberId: string) => void;
  onEditSchedule: (member: TeamMember) => void;
}

export const TeamMemberList: React.FC<TeamMemberListProps> = ({ teamMembers, onEdit, onDelete, onEditSchedule }) => {
  const [statuses, setStatuses] = useState<TeamMemberStatus[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatuses();
    const interval = setInterval(loadStatuses, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadStatuses = async () => {
    try {
      const statusData = await timetablesService.getTeamMemberStatuses();
      setStatuses(statusData);
    } catch (error) {
      console.error('Failed to load statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusForMember = (memberId: string) => {
    return statuses.find(status => status.memberId === memberId);
  };

  const formatNextOnline = (nextOnline: string | null) => {
    if (!nextOnline) return 'No upcoming schedule';

    const date = new Date(nextOnline);
    const now = new Date();
    const diffHours = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60));

    if (diffHours < 24) {
      return `in ${diffHours}h`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  if (loading) {
    return (
      <div className='dashboard-card'>
        <div className='dashboard-card__content' style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div className='pulse' style={{ display: 'inline-block' }}>
            <div
              style={{
                fontSize: '2rem',
                marginBottom: '16px',
                opacity: 0.5,
              }}
            >
              ⏳
            </div>
            <p
              style={{
                color: 'var(--light-gray)',
                fontSize: '0.875rem',
                margin: 0,
              }}
            >
              Loading team member statuses...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard-grid'>
      {teamMembers.map(member => {
        const status = getStatusForMember(member.id);

        return (
          <div key={member.id} className='dashboard-card scale-on-hover'>
            <div className='dashboard-card__header'>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: status?.isOnline ? 'var(--success-green)' : 'var(--medium-gray)',
                    flexShrink: 0,
                  }}
                />
                <div>
                  <h3 className='dashboard-card__title' style={{ margin: 0 }}>
                    {member.name}
                  </h3>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--light-gray)',
                      marginTop: '4px',
                    }}
                  >
                    {member.timezone}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => onEditSchedule(member)}
                  style={{
                    backgroundColor: 'var(--blue-accent)',
                    color: 'var(--white-text)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = 'var(--blue-accent-light)')}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = 'var(--blue-accent)')}
                >
                  Edit Schedule
                </button>
                <button
                  onClick={() => onEdit(member)}
                  style={{
                    backgroundColor: 'var(--warning-amber)',
                    color: 'var(--white-text)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
                  onMouseOut={e => (e.currentTarget.style.opacity = '1')}
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(member.id)}
                  style={{
                    backgroundColor: 'var(--light-red)',
                    color: 'var(--white-text)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = 'var(--primary-red)')}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = 'var(--light-red)')}
                >
                  Delete
                </button>
              </div>
            </div>

            <div className='dashboard-card__content'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: status?.isOnline ? 'var(--success-green)' : 'var(--medium-gray)',
                    }}
                  >
                    {status?.isOnline ? '🟢 Online' : '🔴 Offline'}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--light-gray)',
                    textAlign: 'right',
                  }}
                >
                  {Object.keys(member.schedule).length} days scheduled
                </div>
              </div>

              {!status?.isOnline && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--light-gray)',
                    padding: '8px 12px',
                    backgroundColor: 'var(--tertiary-bg)',
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--blue-accent)',
                  }}
                >
                  <strong>Next online:</strong> {formatNextOnline(status?.nextOnline || null)}
                </div>
              )}

              {status?.isOnline && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--success-green)',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(39, 174, 96, 0.1)',
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--success-green)',
                  }}
                >
                  <strong>Currently available</strong>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {teamMembers.length === 0 && (
        <div className='dashboard-card' style={{ gridColumn: '1 / -1' }}>
          <div className='dashboard-card__content' style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div
              style={{
                fontSize: '3rem',
                marginBottom: '16px',
                opacity: 0.5,
              }}
            >
              👥
            </div>
            <h3
              style={{
                fontSize: '1.125rem',
                color: 'var(--white-text)',
                margin: '0 0 8px 0',
              }}
            >
              No Team Members Yet
            </h3>
            <p
              style={{
                color: 'var(--light-gray)',
                fontSize: '0.875rem',
                margin: 0,
              }}
            >
              Create your first team member to get started with scheduling.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
